'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { SliceZone } from '@prismicio/react';
import { asLink, type LinkField } from '@prismicio/client';
import { DevsuLogoNavbar } from '@/assets/DevsuLogoNavbar';
import { getNavigationComponents } from '@/slices/component-factory';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ArrowRightIcon, CloseIcon, HamburgerIcon } from '@/assets';
import { VelxMarkIcon } from '@/assets/VelxMarkIcon';
import { useMenuContext } from '@/context/MenuContext';
import { useIsClient, useModalLinkDispatcher } from '@/hooks';
import { useModalStore } from '@/store';
import { MODAL_IDS } from '@/utils/modal-registry';
import { resolvePageUrl } from '@/utils/page-url';
import { ModalHost } from './ModalHost';
import { Slice as PrismicSlice, AnyRegularField } from '@prismicio/types';

interface CustomSlice extends PrismicSlice<
  string,
  Record<string, AnyRegularField>,
  Record<string, AnyRegularField>
> {
  id: string;
  variation: string;
  primary: {
    tag?: string;
  };
}

interface GlobalNavBarRedesignProps {
  data: Record<string, unknown>;
  solidBackground?: boolean;
}

type VelxNavLink = { label: string; href: string; description?: string };
type VelxNavColumn = { title?: string; links: VelxNavLink[] };
type VelxNavItem = {
  label: string;
  href: string;
  dropdown?: {
    sidebarTitle: string;
    sidebarDescription?: string;
    sidebarCta?: { label: string; href: string };
    sidebarShowLogo?: boolean;
    columns: VelxNavColumn[];
  };
};

type PrismicLinkField = LinkField | null | undefined;

// Prismic's route resolver has no concept of the `virtual-parent:<segment>`
// tag convention (see `src/utils/page-url.ts`), so `asLink` on a `page` doc
// with a virtual parent returns `/uid` instead of `/segment/uid`. Route
// `page` document links through `resolvePageUrl` — the same helper used by
// `[...uid]/page.tsx`, sitemap and preview — so nav links to
// virtual-parented pages don't 404.
const resolveHref = (link: unknown): string => {
  const linkField = link as PrismicLinkField;
  if (!linkField) {
    return '#';
  }
  if (
    linkField.link_type === 'Document' &&
    linkField.type === 'page' &&
    linkField.uid
  ) {
    const resolved = resolvePageUrl({
      uid: linkField.uid,
      url: linkField.url ?? null,
      tags: linkField.tags,
    });
    if (resolved) {
      return resolved;
    }
  }
  return asLink(linkField) ?? '#';
};

/**
 * Builds the Velx nav structure from the Prismic `global_navigation` slices,
 * recreating the original (pre-Velx) navbar logic: `menu_link` slices are the
 * top-level items and each `withSubMenu` item is matched to its `submenu`
 * slices through the shared `tag` field. The visual design is unchanged — this
 * only feeds the same `VelxNavItem[]` shape the render code already consumes.
 */
const buildVelxNavItems = (slices: CustomSlice[]): VelxNavItem[] => {
  // Map `tag` -> submenu slices (same pairing the old navbar did via context).
  const submenusByTag = new Map<string, CustomSlice[]>();
  slices.forEach((slice) => {
    if (slice.slice_type !== 'submenu') {
      return;
    }
    const tag = (slice.primary as { tag?: string }).tag ?? 'default';
    const list = submenusByTag.get(tag) ?? [];
    list.push(slice);
    submenusByTag.set(tag, list);
  });

  // "Resources" moves from its own top-level chip item into the About Us
  // dropdown (as the second link) to match the redesigned menu, which only
  // has 4 top-level items (Solutions / Velx / Our clients / About us).
  let resourcesLink: VelxNavLink | undefined;
  slices.forEach((slice) => {
    if (slice.slice_type !== 'menu_link' || slice.variation !== 'default') {
      return;
    }
    const primary = slice.primary as Record<string, unknown>;
    if ((primary.label as string) === 'Resources') {
      resourcesLink = {
        label: 'Resources',
        href: resolveHref(primary.link),
      };
    }
  });

  const items: VelxNavItem[] = [];

  slices.forEach((slice) => {
    if (slice.slice_type !== 'menu_link') {
      return;
    }

    const primary = slice.primary as Record<string, unknown>;
    const label = (primary.label as string) ?? '';

    // `bordered` is the "Get Started" CTA — rendered separately by renderVelxCta.
    if (slice.variation === 'bordered') {
      return;
    }

    // Rendered inside the About Us dropdown instead (see below).
    if (slice.variation === 'default' && label === 'Resources') {
      return;
    }

    if (slice.variation === 'withSubMenu') {
      const tag = (primary.tag as string) ?? 'default';
      const submenus = submenusByTag.get(tag) ?? [];

      let sidebarTitle: string | undefined;
      let sidebarShowLogo = false;
      let sidebarDescription: string | undefined;
      let sidebarCta: { label: string; href: string } | undefined;
      const columns: VelxNavColumn[] = [];

      submenus.forEach((sub) => {
        const sp = sub.primary as Record<string, unknown>;

        const velx = sp.velx as { url?: string } | undefined;
        if (velx?.url) {
          sidebarShowLogo = true;
        }

        const velxLink = sp.velx_link as
          | (Record<string, unknown> & { text?: string })
          | undefined;
        const velxHref = velxLink ? asLink(velxLink as PrismicLinkField) : null;
        if (velxHref) {
          sidebarCta = {
            label: (velxLink?.text as string) || 'Velx overview',
            href: velxHref,
          };
        }

        if (typeof sp.description === 'string' && sp.description) {
          sidebarDescription = sp.description;
        }

        // Dedicated field for the dropdown's own sidebar heading, kept
        // independent from both the top nav item's label and the column's
        // own `title` (e.g. "How we engage") — added to the Submenu slice
        // model specifically so these three texts can differ.
        if (typeof sp.sidebar_title === 'string' && sp.sidebar_title) {
          sidebarTitle = sp.sidebar_title;
        }

        const elements =
          (sp.element as Array<Record<string, unknown>> | undefined) ?? [];
        const links: VelxNavLink[] = elements
          .map((el) => ({
            label: (el.element_label as string) ?? '',
            href: resolveHref(el.element_link),
          }))
          .filter((l) => l.label);

        if (links.length) {
          columns.push({
            title: (sp.title as string) || undefined,
            links,
          });
        }
      });

      if (tag === 'about-us' && resourcesLink) {
        if (columns.length > 0) {
          columns[0].links.splice(1, 0, resourcesLink);
        } else {
          columns.push({ links: [resourcesLink] });
        }
      }

      items.push({
        label,
        href: '#',
        dropdown: {
          sidebarTitle: sidebarTitle || label,
          sidebarDescription,
          sidebarCta,
          sidebarShowLogo,
          columns,
        },
      });
      return;
    }

    // `default` variation = regular link.
    items.push({ label, href: resolveHref(primary.link) });
  });

  return items;
};

export const GlobalNavBarRedesign = ({
  data,
  solidBackground = false,
}: GlobalNavBarRedesignProps) => {
  const pathname = usePathname();
  const { slices } = data as { slices: CustomSlice[] };

  // Nav structure is built from the Prismic `global_navigation` slices
  // (menu_link + submenu paired by `tag`), recreating the original navbar
  // logic without changing the Velx visual design.
  const velxNavItems = useMemo(() => buildVelxNavItems(slices), [slices]);

  // Pages that should use the solid dark navbar background
  const isDarkNavPage = pathname === '/new-home';

  // Solid brand-teal background only on dark-nav pages or when explicitly
  // requested. Blog article pages now keep the same translucent + blur
  // (glassy floating) Velx-style navbar as the rest of the site — the previous
  // blog-article solid-background override was removed.
  const useSolidBackground = solidBackground || isDarkNavPage;
  // Only the Get Started modal still consumes a Prismic slice; the rest of
  // the navigation is rendered from the hardcoded Velx config.
  const getStartedFormSlice = slices.find(
    (slice) => slice.slice_type === 'feature_card_grid',
  );
  const components = getNavigationComponents();
  const { isMobile, isMenuOpen, toggleSideBar } = useMenuContext();
  const openModal = useModalStore((state) => state.openModal);

  // The navbar hosts the modal, so it also installs the global entry point that
  // lets any Prismic-authored CTA request it.
  useModalLinkDispatcher();

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  // `lastShownDropdown` survives a close (openDropdown going to null) so the
  // panel content stays rendered while the wrapper fades out. Without this the
  // content would unmount and the backdrop-blur would have to recompute every
  // open, which produced a visible flicker.
  const [lastShownDropdown, setLastShownDropdown] = useState<string | null>(
    null,
  );
  // SSR-safe mounted flag for portal rendering (false on server + first client
  // render, true after hydration) — avoids setState-in-effect.
  const portalMounted = useIsClient();
  const dropdownCloseTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Anchors the dropdown panel to the actual rendered center of the nav
  // links chip (rather than approximating it with viewport centering), so
  // it stays aligned with the chip even though the chip itself isn't
  // perfectly centered in the navbar (the logo and CTA chips differ in
  // width).
  const navChipRef = useRef<HTMLElement>(null);
  const [navChipCenter, setNavChipCenter] = useState<number | null>(null);
  const [navChipWidth, setNavChipWidth] = useState<number | null>(null);

  useEffect(() => {
    const el = navChipRef.current;
    if (!el) {
      return;
    }

    const updateMetrics = () => {
      const rect = el.getBoundingClientRect();
      setNavChipCenter(rect.left + rect.width / 2);
      setNavChipWidth(rect.width);
    };

    updateMetrics();
    window.addEventListener('resize', updateMetrics);
    const observer = new ResizeObserver(updateMetrics);
    observer.observe(el);
    return () => {
      window.removeEventListener('resize', updateMetrics);
      observer.disconnect();
    };
  }, []);

  // Persist the most recently opened dropdown so the panel keeps its content
  // while it fades out (openDropdown -> null). React supports updating state
  // during render to track a previous value; this happens synchronously before
  // commit (no extra paint) and is not a setState-in-effect.
  // When openDropdown becomes null we keep `lastShownDropdown` so the panel can
  // fade out with its previous content intact.
  if (openDropdown && openDropdown !== lastShownDropdown) {
    setLastShownDropdown(openDropdown);
  }

  const openDropdownFor = useCallback((label: string) => {
    if (dropdownCloseTimerRef.current) {
      clearTimeout(dropdownCloseTimerRef.current);
      dropdownCloseTimerRef.current = null;
    }
    setOpenDropdown(label);
  }, []);

  const scheduleCloseDropdown = useCallback(() => {
    if (dropdownCloseTimerRef.current) {
      clearTimeout(dropdownCloseTimerRef.current);
    }
    dropdownCloseTimerRef.current = setTimeout(() => {
      setOpenDropdown(null);
      dropdownCloseTimerRef.current = null;
    }, 150);
  }, []);

  useEffect(() => {
    return () => {
      if (dropdownCloseTimerRef.current) {
        clearTimeout(dropdownCloseTimerRef.current);
      }
    };
  }, []);

  const renderVelxNavItems = (mobile = false) => (
    <>
      {velxNavItems.map((item) => {
        const hasDropdown = !!item.dropdown;
        const isOpenItem = openDropdown === item.label;
        const triggerContent = (
          <span className="inline-flex items-center gap-1.5">
            <span
              className={
                !mobile && isOpenItem
                  ? 'underline decoration-2 underline-offset-[5px] [text-decoration-skip-ink:none]'
                  : ''
              }
            >
              {item.label}
            </span>
            {hasDropdown && (
              <svg
                className={`${mobile ? 'h-4 w-4' : 'h-3 w-3'} transition-transform duration-200 ${
                  isOpenItem ? 'rotate-180' : 'rotate-0'
                }`}
                viewBox="0 0 12 12"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3 4.5l3 3 3-3"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </span>
        );

        const triggerClass = `relative whitespace-nowrap py-1 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#05f273]/60 ${
          isOpenItem ? 'text-[#05f273]' : 'text-white hover:text-[#05f273]'
        }`;

        return (
          <li
            key={item.label}
            className={
              mobile
                ? 'flex w-full flex-col items-center py-1 text-[18px] font-medium text-white'
                : 'group relative flex h-full items-center xl:text-[16px] lg:text-[14px] font-medium'
            }
            onMouseEnter={() => {
              if (!mobile && hasDropdown) {
                openDropdownFor(item.label);
              }
            }}
            onMouseLeave={() => {
              if (!mobile && hasDropdown) {
                scheduleCloseDropdown();
              }
            }}
          >
            {hasDropdown ? (
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={isOpenItem}
                onClick={() =>
                  isOpenItem
                    ? setOpenDropdown(null)
                    : openDropdownFor(item.label)
                }
                className={triggerClass}
              >
                {triggerContent}
              </button>
            ) : (
              <Link
                href={item.href}
                onClick={mobile ? toggleSideBar : undefined}
                className={triggerClass}
              >
                {triggerContent}
              </Link>
            )}
            {mobile && hasDropdown && isOpenItem && item.dropdown && (
              <div className="mt-3 flex w-full flex-col items-center gap-3">
                {item.dropdown.sidebarCta && (
                  <Link
                    href={item.dropdown.sidebarCta.href}
                    onClick={toggleSideBar}
                    className="text-base font-normal text-white hover:text-[#05f273]"
                  >
                    {item.dropdown.sidebarCta.label}
                  </Link>
                )}
                {item.dropdown.columns.map((col, ci) => (
                  <div key={ci} className="flex flex-col gap-2">
                    {col.title && (
                      <span className="text-xs font-medium uppercase tracking-wide text-[#03BF9E]">
                        {col.title}
                      </span>
                    )}
                    {col.links.map((link) => (
                      <Link
                        key={`${link.label}-${link.href}`}
                        href={link.href}
                        onClick={toggleSideBar}
                        className="text-base font-normal text-white hover:text-[#05f273]"
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </li>
        );
      })}
    </>
  );

  const renderVelxCta = (mobile = false) => (
    <button
      type="button"
      onClick={() => openModal()}
      className={`group/cta-btn inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-[8px] bg-[#05f273] px-6 py-3 lg:h-12 lg:py-0 font-inter text-[16px] lg:text-[12px] xl:text-[16px] font-medium leading-6 text-[#002726] transition-colors hover:bg-[#59B264] focus-visible:bg-[#59B264] active:bg-[#59B264] ${
        mobile ? 'mt-2' : ''
      }`}
    >
      <span className="underline-offset-2 group-hover/cta-btn:underline group-focus-visible/cta-btn:underline group-active/cta-btn:underline">
        Get Started
      </span>
    </button>
  );

  const openItem = velxNavItems.find((i) => i.label === openDropdown);
  // displayItem keeps the most recently shown item so content survives the
  // close transition (openDropdown -> null) without unmounting the wrapper.
  const displayItem =
    openItem ?? velxNavItems.find((i) => i.label === lastShownDropdown);
  const isDropdownOpen = openItem !== undefined;
  const velxDropdownPanel =
    displayItem?.dropdown &&
    portalMounted &&
    typeof window !== 'undefined' &&
    !isMobile
      ? createPortal(
          <div
            className={`fixed z-40 max-w-[calc(100vw-2rem)] pointer-events-none transition-opacity duration-200 ${
              isDropdownOpen ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              top: 'calc(var(--announcement-bar-height, 0px) + 28px + 64px + 12px)',
              left: navChipCenter ?? '50%',
              transform: 'translateX(-50%)',
              width: navChipWidth ?? 631,
            }}
          >
            <div
              onMouseEnter={() => openDropdownFor(displayItem.label)}
              onMouseLeave={scheduleCloseDropdown}
              className={`w-full overflow-hidden rounded-[5px] bg-[#e0e0e0] p-2 shadow-[0px_20px_24px_-4px_rgba(0,0,0,0.08),0px_8px_8px_-4px_rgba(0,0,0,0.03)] backdrop-blur-[47.5px] ${
                isDropdownOpen ? 'pointer-events-auto' : 'pointer-events-none'
              }`}
            >
              <div className="flex flex-col gap-2 md:flex-row">
                <div className="flex w-full shrink-0 flex-col items-start justify-between gap-2 self-stretch rounded-[5px] bg-white p-4 backdrop-blur-[30px] md:w-[250px]">
                  <div className="flex w-full flex-col items-start gap-2">
                    {displayItem.dropdown.sidebarShowLogo && (
                      <VelxMarkIcon
                        className="h-[28px] w-[31px]"
                        width={31}
                        height={28}
                      />
                    )}
                    <h3 className="m-0 w-full font-inter text-[20px] font-medium leading-[1.2] text-[#041118]">
                      {displayItem.dropdown.sidebarTitle}
                    </h3>
                    {displayItem.dropdown.sidebarDescription && (
                      <p className="m-0 w-full whitespace-normal break-words font-inter text-[16px] font-light leading-[1.4] text-[#041118]">
                        {displayItem.dropdown.sidebarDescription}
                      </p>
                    )}
                  </div>
                  {displayItem.dropdown.sidebarCta && (
                    <Link
                      href={displayItem.dropdown.sidebarCta.href}
                      className="group/cta flex items-center gap-1 text-[16px] font-medium leading-[1.5] text-[#353535] transition-colors hover:text-[#041118] focus-visible:text-[#041118] active:text-[#041118]"
                    >
                      <ArrowRightIcon className="size-[14px] text-[#4B0FEA]" />
                      <span className="decoration-[#4B0FEA] decoration-2 underline-offset-2 group-hover/cta:font-bold group-hover/cta:underline group-focus-visible/cta:font-bold group-focus-visible/cta:underline group-active/cta:font-bold group-active/cta:underline">
                        {displayItem.dropdown.sidebarCta.label}
                      </span>
                    </Link>
                  )}
                </div>

                <div className="flex min-w-0 flex-1 flex-col items-start">
                  {displayItem.dropdown.columns.map((col, i) => (
                    <div
                      key={i}
                      className="flex w-full min-w-0 flex-col items-start"
                    >
                      {col.title && (
                        <div className="flex w-full items-center px-4 pb-1">
                          <span className="flex-1 font-inter text-[13px] font-medium uppercase leading-[1.5] text-[#353535]">
                            {col.title}
                          </span>
                        </div>
                      )}
                      {col.links.map((link) => (
                        <Link
                          key={`${link.label}-${link.href}`}
                          href={link.href}
                          className="group/link flex w-full items-center gap-2 self-stretch rounded-[8px] p-4 font-inter text-[18px] font-normal leading-[1.2] text-[#041118] transition-colors hover:bg-[#F3F3F3] focus-visible:bg-[#F3F3F3] active:bg-[#F3F3F3]"
                        >
                          <ArrowRightIcon className="size-[14px] shrink-0 text-[#4B0FEA] opacity-0 transition-opacity group-hover/link:opacity-100 group-focus-visible/link:opacity-100 group-active/link:opacity-100" />
                          <span className="font-normal decoration-[#4B0FEA] decoration-2 underline-offset-2 group-hover/link:font-bold group-hover/link:underline group-focus-visible/link:font-bold group-focus-visible/link:underline group-active/link:font-bold group-active/link:underline">
                            {link.label}
                          </span>
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  // Exact Figma chip spec: rgba(76,76,76,0.5) fill + blur, confined to the
  // chip's own bounds (the outer wrapper/gaps stay blur- and fill-free).
  // (Solid mode, used on blog articles / new-home, is intentionally an
  // opaque brand-colour bar — a separate, deliberate exception.)
  const chipBgClass = useSolidBackground
    ? 'lg:bg-[#002726]'
    : 'lg:bg-[rgba(76,76,76,0.5)]';

  return (
    <div
      className={`flex items-center justify-between w-full h-full rounded-[16px] px-[24px] lg:gap-3 lg:px-0 ${
        useSolidBackground
          ? 'bg-[#002726] lg:bg-transparent'
          : 'bg-black/30 lg:bg-transparent'
      }`}
    >
      {velxDropdownPanel}
      {/* Logo chip — plain on mobile, its own translucent rounded chip on
          desktop (matches the source design's 3-chip navbar layout). Blur
          lives only on the chips themselves, not on the outer wrapper, so
          the transparent gaps between chips stay perfectly clear instead
          of hazing over the whole strip. */}
      <div
        className={`shrink-0 flex items-center justify-center lg:h-12 lg:w-[124px] xl:w-[149px] lg:shrink-0 lg:rounded-[5px] lg:backdrop-blur-[30px] lg:px-[12px] xl:px-[21px] ${chipBgClass}`}
      >
        <Link
          href="/"
          aria-label="Devsu Home Page"
          className="inline-flex items-center leading-none"
        >
          <DevsuLogoNavbar className="block aspect-[107/25] h-5 w-auto min-[400px]:h-6 lg:h-[23.778px]" />
        </Link>
      </div>

      <nav
        ref={navChipRef}
        aria-label="Devsu main navigation"
        className={`hidden lg:inline-flex lg:shrink-0 h-12 items-center justify-center rounded-[5px] backdrop-blur-[30px] px-[14px] ${chipBgClass}`}
      >
        <ul className="z-40 flex items-center justify-center xl:gap-[60px] lg:gap-[30px]">
          {renderVelxNavItems()}
        </ul>
      </nav>

      <div className="hidden lg:flex items-center shrink-0">
        {renderVelxCta()}
      </div>

      {/*Sidebar Version*/}
      <div className="lg:hidden flex-1 flex justify-end gap-2 min-[400px]:gap-4 items-center">
        {!isMenuOpen && (
          <div className="hidden min-[340px]:flex min-[340px]:scale-[0.7] min-[400px]:scale-[0.8] min-[500px]:scale-[0.9] sm:scale-100 origin-right">
            {renderVelxCta()}
          </div>
        )}
        <button
          onClick={toggleSideBar}
          type="button"
          className="block lg:hidden shrink-0"
          aria-label="Toggle Navigation Menu"
        >
          {isMenuOpen ? <CloseIcon /> : <HamburgerIcon />}
        </button>
        {portalMounted &&
          typeof window !== 'undefined' &&
          createPortal(
            <section
              className={`${
                isMenuOpen
                  ? 'visible opacity-100'
                  : 'invisible opacity-0 pointer-events-none'
              } fixed inset-0 z-50 lg:hidden transition-opacity duration-200`}
              aria-hidden={!isMenuOpen}
            >
              <div
                className="absolute inset-0 bg-black/60"
                onClick={toggleSideBar}
              />
              <div
                className="absolute top-0 left-0 right-0 flex max-h-[calc(100vh-40px)] flex-col overflow-hidden rounded-b-[24px] bg-black/60 backdrop-blur-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Card header: logo + close */}
                <div className="flex items-center justify-between px-6 pt-12">
                  <Link
                    href="/"
                    aria-label="Devsu Home Page"
                    onClick={toggleSideBar}
                    className="inline-flex items-center leading-none"
                  >
                    <DevsuLogoNavbar className="block aspect-[107/25] h-5 w-auto" />
                  </Link>
                  <button
                    type="button"
                    onClick={toggleSideBar}
                    aria-label="Close menu"
                    className="text-white"
                  >
                    <CloseIcon width={24} height={24} fill="white" />
                  </button>
                </div>

                {/* Menu items — tight spacing so the card ~half viewport */}
                <nav
                  aria-label="Mobile navigation"
                  className="overflow-y-auto overscroll-contain"
                >
                  <ul className="flex flex-col items-center gap-1 px-6 pb-4 pt-4 text-center">
                    {renderVelxNavItems(true)}
                  </ul>
                </nav>

                {/* Bottom CTA */}
                <div className="px-6 pb-6">
                  <button
                    type="button"
                    onClick={() => {
                      toggleSideBar();
                      openModal();
                    }}
                    className="inline-flex w-full items-center justify-center rounded-[8px] bg-[#05f273] px-7 py-3.5 font-inter text-[16px] font-medium leading-6 text-[#002726] transition-opacity hover:opacity-90"
                  >
                    Get Started
                  </button>
                </div>
              </div>
            </section>,
            document.body,
          )}
      </div>
      {/*Sidebar Version*/}

      {/* The navbar owns the Get Started modal; ModalHost is the shared
          lifecycle/chrome it was previously inlining. */}
      {getStartedFormSlice && (
        <ModalHost modalId={MODAL_IDS.getStarted}>
          <SliceZone slices={[getStartedFormSlice]} components={components} />
        </ModalHost>
      )}
    </div>
  );
};
